import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Grid,
  Card,
  Tabs,
  Tab,
  TablePagination,
  Alert,
  Collapse,
  IconButton
} from '@mui/material';
import { Info, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import Layout from './Layout';
import AppContext from './AppContext';
import { startOfToday, startOfDay, endOfDay, format } from 'date-fns';
import { enGB } from 'date-fns/locale';

const ViewStock = () => {
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(startOfToday());
  const [toDate, setToDate] = useState(startOfToday());
  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const { shopId, t } = useContext(AppContext);

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch products and sales data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch products
        const productsQuery = query(
          collection(db, 'products'),
          where('shopId', '==', shopId),
          where('productType', '==', 'Sale')
        );
        const productsSnapshot = await getDocs(productsQuery);
        const productsData = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch sales
        const salesQuery = query(
          collection(db, 'sales'),
          where('shopId', '==', shopId),
          where('orderDate', '>=', startOfDay(fromDate)),
          where('orderDate', '<=', endOfDay(toDate))
        );
        const salesSnapshot = await getDocs(salesQuery);
        const salesData = salesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setProducts(productsData);
        setSales(salesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [shopId, fromDate, toDate]);

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
    setPage(0);
  };

  const handleProductClick = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const formatToMyanmarCurrency = (number) => {
    return new Intl.NumberFormat('my-MM').format(number);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredProducts = products.filter((product) => {
    if (!normalizedQuery) {
      return true;
    }

    if (tabIndex === 0) {
      return (product.name || '').toLowerCase().includes(normalizedQuery);
    }

    const stockMatches = Array.isArray(product.stock) && product.stock.some((entry) =>
      String(entry.stockCode || '').toLowerCase().includes(normalizedQuery)
    );

    if (stockMatches) {
      return true;
    }

    return sales.some((sale) =>
      Array.isArray(sale.items) && sale.items.some((item) =>
        item.productId === product.id &&
        String(item.stockCode || '').toLowerCase().includes(normalizedQuery)
      )
    );
  });

  // Add helper function for date validation
const isValidDate = (date) => {
  return date && !isNaN(new Date(date).getTime());
};

// Update getSoldItems function
const getSoldItems = (productId) => {
  return sales.flatMap(sale => 
    sale.items
      .filter(item => item.productId === productId)
      .map(item => ({
        ...item,
        saleId: sale.orderId,
        saleDate: sale.orderDate,
      }))
  );
};

// Add these helper functions
  const calculateStockMovements = (product, sales) => {
    const stockAdded = product.stock?.reduce((sum, entry) => {
      const stockDate = new Date(entry.dateAdded);
      if (stockDate >= startOfDay(fromDate) && stockDate <= endOfDay(toDate)) {
        return sum + Number(entry.quantity);
      }
      return sum;
    }, 0) || 0;

    const stockSold = sales.flatMap(sale => 
      sale.items.filter(item => item.productId === product.id)
    ).reduce((sum, item) => sum + Number(item.quantity), 0);

    return {
      totalAdded: stockAdded,
      totalSold: stockSold
    };
  };

  const getStockMovements = (product, sales) => {
    // Get stock entries within date range
    const stockEntries = product.stock?.filter(entry => {
      const stockDate = new Date(entry.dateAdded);
      return stockDate >= startOfDay(fromDate) && stockDate <= endOfDay(toDate);
    }).map(entry => ({
      stockCode: entry.stockCode,
      quantity: entry.quantity,
      date: new Date(entry.dateAdded),
      status: 'In Stock',
      saleId: null,
      saleDate: null
    })) || [];

    // Get sold items within date range
    const soldItems = sales.flatMap(sale => 
      sale.items
        .filter(item => item.productId === product.id)
        .map(item => ({
          stockCode: item.stockCode,
          quantity: item.quantity,
          date: sale.orderDate.toDate(),
          status: 'Sold',
          saleId: sale.orderId,
          saleDate: sale.orderDate.toDate()
        }))
    );

    // Combine and sort by date
    return [...stockEntries, ...soldItems].sort((a, b) => b.date - a.date);
  };

  // Update the table rendering based on tab
  return (
    <Layout>
      <Box sx={{ p: 3, mt: 0 }}>
        {isOffline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("You are offline. Some features may not be available.")}
          </Alert>
        )}

        <Typography variant="h4" mb={2}>
          {t("Stock Overview")}
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box>
            <Tabs value={tabIndex} onChange={handleChangeTab} sx={{ mb: 2 }}>
              <Tab label={t("Current Stock")} />
              <Tab label={t("Stock Movement")} />
            </Tabs>

            <Grid container spacing={2} alignItems="center" mb={3}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label={t("From")}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label={t("To")}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={tabIndex === 0 ? t("Search Products") : t("Search Stock")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Grid>
            </Grid>

            <TableContainer component={Card} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px" />
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("Product Name")}
                      </Typography>
                    </TableCell>
                    {tabIndex === 0 ? (
                      <>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t("Current Stock")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t("Last Updated")}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t("Total Added")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t("Total Sold")}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => {
                      const movements = calculateStockMovements(product, sales);

                      return (
                        <React.Fragment key={product.id}>
                          <TableRow
                            hover
                            onClick={() => handleProductClick(product.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {expandedProduct === product.id ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>{product.name}</TableCell>
                            {tabIndex === 0 ? (
                              <>
                                <TableCell>
                                  {product.stock?.reduce(
                                    (sum, entry) => sum + Number(entry.quantity),
                                    0
                                  ) || 0}
                                </TableCell>
                                <TableCell>
                                  {product.stock?.length > 0
                                    ? format(
                                        new Date(
                                          product.stock[product.stock.length - 1].dateAdded
                                        ),
                                        'dd/MM/yyyy'
                                      )
                                    : t("N/A")}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>{movements.totalAdded}</TableCell>
                                <TableCell>{movements.totalSold}</TableCell>
                              </>
                            )}
                          </TableRow>

                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                              <Collapse in={expandedProduct === product.id} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  {tabIndex === 0 ? (
                                    // Current Stock Details
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>{t("Stock Code")}</TableCell>
                                          <TableCell>{t("Description")}</TableCell>
                                          <TableCell>{t("Quantity")}</TableCell>
                                          <TableCell>{t("Date Added")}</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {product.stock?.map((stock) => (
                                          <TableRow key={stock.stockCode}>
                                            <TableCell>{stock.stockCode}</TableCell>
                                            <TableCell>{stock.description}</TableCell>
                                            <TableCell>{stock.quantity}</TableCell>
                                            <TableCell>
                                              {format(new Date(stock.dateAdded), 'dd/MM/yyyy')}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    // Stock Movement Details
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>{t("Stock Code")}</TableCell>
                                          <TableCell>{t("Quantity")}</TableCell>
                                          <TableCell>{t("Status")}</TableCell>
                                          <TableCell>{t("Date")}</TableCell>
                                          <TableCell>{t("Sale ID")}</TableCell>
                                          <TableCell>{t("Sale Date")}</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {getStockMovements(product, sales).map((movement, index) => (
                                          <TableRow 
                                            key={`${movement.stockCode}-${index}`}
                                            sx={{
                                              backgroundColor: movement.status === 'Sold' ? '#fff8e1' : 'inherit'
                                            }}
                                          >
                                            <TableCell>{movement.stockCode}</TableCell>
                                            <TableCell>{movement.quantity}</TableCell>
                                            <TableCell>{t(movement.status)}</TableCell>
                                            <TableCell>
                                              {format(movement.date, 'dd/MM/yyyy')}
                                            </TableCell>
                                            <TableCell>
                                              {movement.saleId ? String(movement.saleId).padStart(6, '0') : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {movement.saleDate 
                                                ? format(movement.saleDate, 'dd/MM/yyyy hh:mm a')
                                                : '-'
                                              }
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredProducts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </Box>
        </LocalizationProvider>
      </Box>
    </Layout>
  );
};

export default ViewStock;
